/**
 * Course grouping / filtering helpers — pure functions over `Course[]` that
 * back the v3 CoursePanel (find_courses results). Kept side-effect-free and
 * framework-free so every branch is unit-testable without mounting React.
 *
 * Two different "weekend" notions live here on purpose:
 *  - `filterByChip(..., 'weekend')` — ANY course whose start or end date is a
 *    Saturday/Sunday, regardless of which calendar week (the chip is a
 *    general "weekend courses" filter).
 *  - `groupCourses`'s "This weekend" bucket — only courses starting on the
 *    Saturday/Sunday of the week containing the caller-supplied `now`
 *    (a much narrower, session-relative window used for the expanded list's
 *    "coming up first" section).
 */

import type { Course } from '@/types/course';

// ── Filter chips ─────────────────────────────────────────────────────────────

export const CHIP_KEY = {
  ALL: 'all',
  IN_PERSON: 'in-person',
  ONLINE: 'online',
  WEEKEND: 'weekend',
} as const;
export type ChipKey = (typeof CHIP_KEY)[keyof typeof CHIP_KEY];

export interface FilterChipDef {
  key: ChipKey;
  label: string;
}

// ── Grouped-view types ───────────────────────────────────────────────────────

export type GroupAccent = 'warm' | 'blue';

export interface CourseGroup {
  label: string;
  accent: GroupAccent;
  courses: Course[];
}

// ── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Parse a bare "YYYY-MM-DD" into a local-time Date. Never `new Date(str)` —
 * that UTC-parses a date-only string per spec, which shifts the calendar day
 * backward in any negative-UTC-offset timezone (all of the Americas). Same
 * fix as primitives/date-tile.tsx.
 */
function parseYMD(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toYMD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isWeekendDate(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const day = parseYMD(dateStr).getDay();
  return day === 0 || day === 6;
}

/** The Saturday/Sunday ("YYYY-MM-DD") of the Mon–Sun week containing `now`. */
function thisWeekendRange(now: Date): { saturday: string; sunday: string } {
  const dow = now.getDay(); // 0 Sun .. 6 Sat
  const daysSinceMonday = (dow + 6) % 7;
  const monday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - daysSinceMonday
  );
  const saturday = new Date(
    monday.getFullYear(),
    monday.getMonth(),
    monday.getDate() + 5
  );
  const sunday = new Date(
    monday.getFullYear(),
    monday.getMonth(),
    monday.getDate() + 6
  );
  return { saturday: toYMD(saturday), sunday: toYMD(sunday) };
}

// ── Core helpers ─────────────────────────────────────────────────────────────

/**
 * Sort by `eventStartDate` ascending. "YYYY-MM-DD" strings compare correctly
 * with plain `<`/`>` (lexicographic order matches chronological order for
 * this format), so no Date parsing is needed. Courses with no start date sort
 * last rather than being dropped. Returns a new array — never mutates.
 */
export function sortSoonest(courses: Course[]): Course[] {
  return [...courses].sort((a, b) => {
    const aDate = a.eventStartDate;
    const bDate = b.eventStartDate;
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    if (aDate < bDate) return -1;
    if (aDate > bDate) return 1;
    return 0;
  });
}

/**
 * Normalize the backend's free-text `mode` (observed as "In Person" /
 * "In-Person" / "Online", casing not guaranteed) to a closed two-value union.
 * Mirrors the V2 course tile's `mode.toLowerCase() === 'in person'`
 * check but also strips hyphens so "In-Person" matches too. Anything that
 * isn't recognizably in-person is treated as online — the only other value
 * the backend sends today, so this is a safe default rather than a guess.
 */
export function courseMode(course: Course): 'in-person' | 'online' {
  const normalized = (course.mode || '').toLowerCase().replace(/[\s-]/g, '');
  return normalized === 'inperson' ? 'in-person' : 'online';
}

function isWeekendCourse(course: Course): boolean {
  return (
    isWeekendDate(course.eventStartDate) || isWeekendDate(course.eventEndDate)
  );
}

/** Filter courses by a chip key. Unknown keys fall through to 'all' (no-op) — fail open rather than blanking the list on a typo'd key. */
export function filterByChip(
  courses: Course[],
  chipKey: ChipKey | string
): Course[] {
  switch (chipKey) {
    case CHIP_KEY.IN_PERSON:
      return courses.filter((c) => courseMode(c) === 'in-person');
    case CHIP_KEY.ONLINE:
      return courses.filter((c) => courseMode(c) === 'online');
    case CHIP_KEY.WEEKEND:
      return courses.filter(isWeekendCourse);
    default:
      return courses;
  }
}

/**
 * Ordered groups for the expanded CoursePanel view:
 *   1. "This weekend" — in-person courses starting on the current week's
 *      Sat/Sun (relative to `now`), if any.
 *   2. One group per (year, month) for the remaining in-person courses,
 *      chronological. The bucket key includes the year so two Novembers a
 *      year apart never collide into one out-of-order group — but per the
 *      v3 design the visible label is still the bare month name ("October"),
 *      since search results realistically never span more than a year.
 *   3. "Dates TBD" — in-person courses with no `eventStartDate` at all.
 *      Not in the design spec, but courses must never silently vanish from
 *      the expanded view just because the backend omitted a date.
 *   4. "Online · anytime" — every online course, regardless of date.
 * Empty groups are omitted entirely.
 */
export function groupCourses(courses: Course[], now: Date): CourseGroup[] {
  const online: Course[] = [];
  const inPerson: Course[] = [];
  for (const course of courses) {
    (courseMode(course) === 'online' ? online : inPerson).push(course);
  }

  const { saturday, sunday } = thisWeekendRange(now);
  const weekend: Course[] = [];
  const monthed: Course[] = [];
  const undated: Course[] = [];

  for (const course of inPerson) {
    const start = course.eventStartDate;
    if (!start) {
      undated.push(course);
    } else if (start === saturday || start === sunday) {
      weekend.push(course);
    } else {
      monthed.push(course);
    }
  }

  const monthBuckets = new Map<string, { label: string; courses: Course[] }>();
  for (const course of monthed) {
    const parsed = parseYMD(course.eventStartDate as string);
    const key = `${parsed.getFullYear()}-${parsed.getMonth()}`;
    if (!monthBuckets.has(key)) {
      monthBuckets.set(key, {
        label: parsed.toLocaleDateString('en-US', { month: 'long' }),
        courses: [],
      });
    }
    monthBuckets.get(key)!.courses.push(course);
  }
  const monthKeys = Array.from(monthBuckets.keys()).sort((a, b) => {
    const [ay, am] = a.split('-').map(Number);
    const [by, bm] = b.split('-').map(Number);
    return ay - by || am - bm;
  });

  const groups: CourseGroup[] = [];
  if (weekend.length > 0) {
    groups.push({
      label: 'This weekend',
      accent: 'warm',
      courses: sortSoonest(weekend),
    });
  }
  for (const key of monthKeys) {
    const bucket = monthBuckets.get(key)!;
    groups.push({
      label: bucket.label,
      accent: 'warm',
      courses: sortSoonest(bucket.courses),
    });
  }
  if (undated.length > 0) {
    groups.push({ label: 'Dates TBD', accent: 'warm', courses: undated });
  }
  if (online.length > 0) {
    groups.push({
      label: 'Online · anytime',
      accent: 'blue',
      courses: sortSoonest(online),
    });
  }

  return groups;
}

/** Always 'All'; add 'In person' / 'Online' / 'Weekends' only when the set actually contains a matching course, so a single-mode result set doesn't show a chip with nothing behind it. */
export function deriveChips(courses: Course[]): FilterChipDef[] {
  const chips: FilterChipDef[] = [{ key: CHIP_KEY.ALL, label: 'All' }];
  if (courses.some((c) => courseMode(c) === 'in-person')) {
    chips.push({ key: CHIP_KEY.IN_PERSON, label: 'In person' });
  }
  if (courses.some((c) => courseMode(c) === 'online')) {
    chips.push({ key: CHIP_KEY.ONLINE, label: 'Online' });
  }
  if (courses.some(isWeekendCourse)) {
    chips.push({ key: CHIP_KEY.WEEKEND, label: 'Weekends' });
  }
  return chips;
}

/**
 * Human label for WHAT WAS SEARCHED, from find_courses' tool-call arguments
 * (forwarded on tool_end — see types/chat.ts TOOL_END.arguments). Prefers
 * `locations` (multi-city search, joined "Fremont, CA & Cupertino, CA"),
 * then the zip ("07029"), else "City, ST". Null when the args carry no
 * location (the agent searched by lat/long or omitted location entirely).
 * This one deriver covers both the live tool_end path and the
 * restored-conversation path — both forward the same tool-call arguments.
 */
export function searchedLocationFromArgs(
  args: Record<string, unknown> | undefined
): string | null {
  if (!args) return null;
  if (Array.isArray(args.locations)) {
    const cleaned = args.locations
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
    if (cleaned.length > 0) return cleaned.join(' & ');
  }
  const zip = typeof args.zip_code === 'string' ? args.zip_code.trim() : '';
  if (zip) return zip;
  const city = typeof args.city === 'string' ? args.city.trim() : '';
  const state = typeof args.state === 'string' ? args.state.trim() : '';
  if (city) return state ? `${city}, ${state}` : city;
  return null;
}
